"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { useListPagination } from "@/hooks";
import type {
  TopSellersFilters,
  TopSellersFilterState,
} from "@/features/topsellers/types";
import type { FilterExcludeSection } from "@/features/topsellers/components/TopSellersFilters";
import type { QuickFilter } from "../types";
import {
  getDefaultFilterState,
  hasActiveFilters,
  countActiveFilters,
  hasActiveFiltersExcept,
  countActiveFiltersExcept,
} from "@/features/topsellers/utils";
import type { SkeletonColumn } from "@/components/shared/list-skeleton/types";
import { FiltersDrawer } from "./FiltersDrawer";
import { QuickFilterButtons } from "./QuickFilterButtons";
import { useUrlFilters } from "../hooks/useUrlFilters";

export interface RankListWithFiltersProps<T, S extends string | null> {
  initialItems: T[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  initialSort: S;
  /**
   * The filter state the SERVER used to produce `initialItems`. When the page
   * SSRs the filtered list (filter params in the URL), pass the parsed filters
   * here — the client then skips the redundant on-mount re-fetch (no flash, and
   * back-navigation restores the filtered list instantly from the router cache).
   * Omit / default = unfiltered SSR → client re-fetches when filters are active
   * (legacy behaviour, kept for not-yet-migrated pages).
   */
  initialAppliedFilters?: TopSellersFilterState;
  sortable: boolean;
  /**
   * The page's own server-side `?sort=` parser. Lets Back from a detail page
   * restore the URL's sort (and an explicit pageSize), not just its page —
   * see `parseSort` in useListPagination.
   */
  parseSort?: (raw: string | null) => S | null;
  loadUnfiltered: (
    page: number,
    pageSize: number,
    sortBy: S | null,
  ) => Promise<{ items: T[]; total: number }>;
  loadFiltered: (
    page: number,
    pageSize: number,
    sortBy: S | null,
    filters: TopSellersFilterState,
  ) => Promise<{ items: T[]; total: number }>;
  filters: TopSellersFilters;
  skeletonConfig: SkeletonColumn[];
  titleId: string;
  excludeSections?: FilterExcludeSection[];
  /** Контент зліва від кнопки Filters (напр. MonthPicker) */
  headerLeftSlot?: React.ReactNode;
  /** Швидкі кнопки фільтрів (Sexual Content, Free тощо) */
  quickFilters?: QuickFilter[];
  /** Ключ для збереження фільтрів у sessionStorage (напр. при зміні місяця) */
  persistFiltersKey?: string;
  renderList: (props: {
    items: T[];
    sort: S | null | undefined;
    initialSort: S;
    onSortChange: ((s: S | null) => void) | undefined;
    emptyMessage?: string;
  }) => React.ReactNode;
  emptyMessageWhenFiltered?: string;
}

export function RankListWithFilters<T, S extends string | null>({
  initialItems,
  initialPage,
  initialTotal,
  pageSize,
  initialSort,
  initialAppliedFilters,
  sortable,
  parseSort,
  loadUnfiltered,
  loadFiltered,
  filters,
  skeletonConfig,
  titleId,
  excludeSections,
  headerLeftSlot,
  quickFilters = [],
  persistFiltersKey,
  renderList,
  emptyMessageWhenFiltered = "No results found for the selected filters.",
}: RankListWithFiltersProps<T, S>) {
  // URL — single source of truth for filters. persistFiltersKey is preserved
  // in the signature for back-compat (old usePersistedFilters key); the URL-
  // backed hook ignores it but call sites don't need to change.
  const [appliedFilters, setAppliedFilters] = useUrlFilters(persistFiltersKey);

  const [draftFilters, setDraftFilters] = useState<TopSellersFilterState>(
    getDefaultFilterState,
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const ignoreFilterKeys =
    useMemo((): (keyof import("@/features/topsellers/types").TopSellersFilterState)[] => {
      const keys: (keyof import("@/features/topsellers/types").TopSellersFilterState)[] =
        [];
      // comingSoon-excluded pages still SHOW the reduced Early Access section
      // (same releaseStatus field), so releaseStatus stays countable — only
      // the legacy upcomingOnly flag is ignored there.
      if (excludeSections?.includes("comingSoon")) keys.push("upcomingOnly");
      if (excludeSections?.includes("releaseDate"))
        keys.push("releaseFrom", "releaseTo");
      return keys;
    }, [excludeSections]);

  const hasFiltersActive = useMemo(
    () =>
      ignoreFilterKeys.length > 0
        ? hasActiveFiltersExcept(appliedFilters, ignoreFilterKeys)
        : hasActiveFilters(appliedFilters),
    [appliedFilters, ignoreFilterKeys],
  );
  const activeCount = useMemo(
    () =>
      ignoreFilterKeys.length > 0
        ? countActiveFiltersExcept(appliedFilters, ignoreFilterKeys)
        : countActiveFilters(appliedFilters),
    [appliedFilters, ignoreFilterKeys],
  );

  const onPageChange = useCallback(
    async (page: number, nextPageSize: number, sortBy?: S | null) => {
      if (!hasFiltersActive) {
        return loadUnfiltered(page, nextPageSize, sortBy ?? null);
      }
      return loadFiltered(page, nextPageSize, sortBy ?? null, appliedFilters);
    },
    [hasFiltersActive, appliedFilters, loadUnfiltered, loadFiltered],
  );

  const {
    items,
    currentPage,
    currentSort,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handleSortChange,
    handlePageSizeChange,
  } = useListPagination<T, S>({
    initialItems,
    initialPage,
    initialTotal,
    pageSize,
    sortable,
    initialSort,
    onPageChange,
    parseSort,
  });

  // Signature of the filters the SERVER baked into `initialItems`. Stable string
  // (not a fresh object each render) so it can sit in effect deps safely.
  const ssrFilterSig = useMemo(
    () => JSON.stringify(initialAppliedFilters ?? getDefaultFilterState()),
    [initialAppliedFilters],
  );

  const isFirstRender = useRef(true);
  const prevFiltersRef = useRef<TopSellersFilterState>(appliedFilters);
  const prevInitialItemsRef = useRef(initialItems);
  const sortChangeRef = useRef(handleSortChange);

  useEffect(() => {
    sortChangeRef.current = handleSortChange;
  }, [handleSortChange]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevFiltersRef.current = appliedFilters;
      // The server already produced `initialItems` for `ssrFilterSig`. Re-fetch
      // on mount ONLY if the URL's filters differ from what SSR applied — when
      // they match (SSR-filtered page, or back-nav restoring a filtered RSC),
      // skip → no flash, instant filtered back-navigation, no eternal loader.
      const ssrMatches = JSON.stringify(appliedFilters) === ssrFilterSig;
      if (!ssrMatches && sortChangeRef.current) {
        sortChangeRef.current(currentSort ?? null);
      }
      return;
    }

    const changed =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(appliedFilters);
    if (changed) {
      prevFiltersRef.current = appliedFilters;
      // Filters apply via a real Next navigation now, so the server re-renders
      // with the matching filtered `initialItems` (ssrFilterSig === appliedFilters)
      // → no client re-fetch needed (avoids a double fetch). The re-fetch stays
      // as a fallback for any path where SSR didn't already match the filters.
      const ssrMatches = JSON.stringify(appliedFilters) === ssrFilterSig;
      if (!ssrMatches && sortChangeRef.current) {
        sortChangeRef.current(currentSort ?? null);
      }
    }
  }, [appliedFilters, currentSort, ssrFilterSig]);

  // Server-driven refresh of initialItems (e.g. monthly's MonthPicker URL
  // change, or upcoming-releases' ?q= search). Without this, dropping `key=`
  // from those pages would leave client-side filters unapplied — the parent
  // re-renders with new initialItems (no filters baked in), useListPagination
  // syncs them, but the appliedFilters useEffect above doesn't fire because
  // filter state didn't change. Re-run the filter pipeline so the table
  // ends up with the right (filters + new server context) data.
  // BUT when the page SSRs already-filtered data (ssrFilterSig === the active
  // filters), the new initialItems are correct as-is → skip the redundant
  // re-fetch (keeps back-nav / month-change instant).
  useEffect(() => {
    if (prevInitialItemsRef.current === initialItems) return;
    prevInitialItemsRef.current = initialItems;
    if (isFirstRender.current) return; // first render is handled above
    const ssrMatches = JSON.stringify(appliedFilters) === ssrFilterSig;
    if (!ssrMatches && hasFiltersActive && sortChangeRef.current) {
      sortChangeRef.current(currentSort ?? null);
    }
  }, [initialItems, hasFiltersActive, currentSort, appliedFilters, ssrFilterSig]);

  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFiltersOpen(true);
  };

  const closeFilters = () => setIsFiltersOpen(false);
  const clearFilters = () => setAppliedFilters(getDefaultFilterState());
  const resetDraft = () => setDraftFilters(getDefaultFilterState());

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setIsFiltersOpen(false);
  };

  const emptyMessage = hasFiltersActive ? emptyMessageWhenFiltered : undefined;

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between">
        <div className="overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible md:pb-0">
          <div className="flex flex-nowrap items-center gap-2 md:gap-3 md:flex-wrap min-w-max md:min-w-0">
            {headerLeftSlot}
            <QuickFilterButtons
            filters={quickFilters}
            appliedFilters={appliedFilters}
            onToggle={setAppliedFilters}
          />
          <button
            type="button"
            onClick={openFilters}
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#2a475e]/50 bg-[#16202d]/50 text-[#c7d5e0] hover:bg-[#1f2e3f]/50 hover:border-[#3a556e] transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4 text-[#8f98a0]" />
            <span className="text-sm font-medium">Filters</span>
            {activeCount > 0 && (
              <span className="ml-1 text-xs font-semibold px-1.5 py-0.5 rounded bg-[#1f2e3f] text-[#66c0f4]">
                {activeCount}
              </span>
            )}
          </button>
          </div>
        </div>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="h-10 px-3 rounded-lg border border-[#2a475e]/50 bg-transparent text-[#8f98a0] hover:text-white hover:bg-[#1f2e3f]/50 transition-colors text-sm font-medium"
          >
            Clear
          </button>
        )}
      </div>

      <FiltersDrawer
        isOpen={isFiltersOpen}
        onClose={closeFilters}
        titleId={titleId}
        filters={filters}
        value={draftFilters}
        onChange={setDraftFilters}
        onReset={resetDraft}
        onApply={applyFilters}
        excludeSections={excludeSections}
      />

      <div className="space-y-4">
        {isPending ? (
          <ListSkeleton columns={skeletonConfig} />
        ) : (
          renderList({
            items,
            sort: currentSort,
            initialSort,
            onSortChange: handleSortChange,
            emptyMessage,
          })
        )}

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={currentPageSize}
          onPageSizeChange={handlePageSizeChange!}
          disabled={isPending}
        />
      </div>
    </div>
  );
}
